import React from "react";
import {
  SFStack,
  getStyled,
  SFTypography,
  SFButton,
  SFCopilotIcon,
  SFIconButton,
  SFCloseIcon,
  SFArrowForwardIcon,
  SFAsyncActionButton
} from "@sf-core";
import PropTypes from "prop-types";

const { styled } = getStyled();

const SFStackStyled = styled(SFStack)(({ theme }) => ({
  padding: theme.spacing(2),
  borderRadius: 4,
  border: `1px solid ${theme.palette.grey300.main}`,
  background: theme.palette.grey50.main,
  boxShadow: "0px 4px 4px 0px rgba(0, 0, 0, 0.25)",
  ":hover": {
    border: `1px solid ${theme.palette.blue400.main}`,
  },
}));

const SFStyledButton = styled(SFButton)(({ theme }) => ({
  background: theme.palette.blue400.main,
  ":hover": {
    background: theme.palette.white.main,
    borderColor: theme.palette.blue400.main,
    color: theme.palette.blue400.main,
    "& svg": {
      color: theme.palette.blue400.main,
    },
  },
}));

const SFStyledAsyncButton = styled(SFAsyncActionButton)(({ theme }) => ({
  background: theme.palette.blue400.main,
  ":hover": {
    background: theme.palette.white.main,
    borderColor: theme.palette.blue400.main,
    color: theme.palette.blue400.main,
    "& svg": {
      color: theme.palette.blue400.main,
    },
  },
}));

const SFStyledSecondaryButton = styled(SFButton)(({ theme }) => ({
  ":hover": {
    background: theme.palette.blue400.main,
    color: theme.palette.white.main,
  },
  color: theme.palette.blue500.main,
  borderColor: theme.palette.blue500.main,
}));

const SFCopilotBanner = ({
  open = false,
  onClose,
  message,
  title = null,
  onPrimaryClick = () => {},
  onSecondaryClick = () => {},
  primaryButtonLabel = null,
  secondaryButtonLabel = null,
  isVertical = false,
  primaryButtonDisabled = false,
  isAsync = false, // New prop to determine if the primary button is async
  loading = false,
}) => {
  if (!open) return null;

  return (
    <SFStackStyled
      spacing={2}
      direction="row"
      alignItems="center"
      justifyContent="space-between"
    >
      <SFStack direction="row" alignItems="center" spacing={2}>
        <SFCopilotIcon color="blue500" size="large" />
        <SFStack
          direction="column"
          alignItems="flex-start"
          justifyContent="space-between"
          spacing={1}
        >
          <SFStack direction="column" justifyContent="center">
            {title && <SFTypography variant="body1bold">{title}</SFTypography>}
            <SFTypography variant="body1">{message}</SFTypography>
          </SFStack>
          {isVertical && (
            <SFStack direction="row" alignItems="center" spacing={2}>
              {isAsync ? (
                <SFStyledAsyncButton
                  variant="primary"
                  onClick={onPrimaryClick}
                  endIcon={loading ? null : <SFArrowForwardIcon color="white" />}
                  disabled={primaryButtonDisabled}
                  loading={loading}
                  
                >
                  {primaryButtonLabel}
                </SFStyledAsyncButton>
              ) : (
                <SFStyledButton
                  variant="primary"
                  onClick={onPrimaryClick}
                  endIcon={<SFArrowForwardIcon color="white" />}
                  disabled={primaryButtonDisabled}
                >
                  {primaryButtonLabel}
                </SFStyledButton>
              )}
              {secondaryButtonLabel && (
                <SFStyledSecondaryButton
                  variant="secondary"
                  onClick={onSecondaryClick}
                >
                  {secondaryButtonLabel}
                </SFStyledSecondaryButton>
              )}
            </SFStack>
          )}
        </SFStack>
      </SFStack>
      <SFStack direction="row" alignItems="center" spacing={2}>
        {!isVertical && (
          <SFStack direction="row" alignItems="center" spacing={2}>
            {isAsync ? (
              <SFStyledAsyncButton
                variant="primary"
                onClick={onPrimaryClick}
                endIcon={loading ? null : <SFArrowForwardIcon color="white" />}
                disabled={primaryButtonDisabled}
                loading={loading}
              >
                {primaryButtonLabel}
              </SFStyledAsyncButton>
            ) : (
              <SFStyledButton
                variant="primary"
                onClick={onPrimaryClick}
                endIcon={<SFArrowForwardIcon color="white" />}
                disabled={primaryButtonDisabled}
              >
                {primaryButtonLabel}
              </SFStyledButton>
            )}
            {secondaryButtonLabel && (
              <SFStyledSecondaryButton
                variant="secondary"
                onClick={onSecondaryClick}
              >
                {secondaryButtonLabel}
              </SFStyledSecondaryButton>
            )}
          </SFStack>
        )}
        <SFIconButton variant="transparent" onClick={onClose}>
          <SFCloseIcon />
        </SFIconButton>
      </SFStack>
    </SFStackStyled>
  );
};

SFCopilotBanner.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onPrimaryClick: PropTypes.func.isRequired,
  onSecondaryClick: PropTypes.func.isRequired,
  title: PropTypes.string,
  message: PropTypes.string,
  primaryButtonLabel: PropTypes.string,
  secondaryButtonLabel: PropTypes.string,
  isVertical: PropTypes.bool,
  primaryButtonDisabled: PropTypes.bool,
  isAsync: PropTypes.bool,
  loading: PropTypes.bool 
};

export default SFCopilotBanner;
